/**
 * Toast styling utility that provides consistent class and style management
 * following PrimeVue's pattern. This utility generates the necessary classes
 * and inline styles for positioning and appearance of toast components.
 */

// Inline styles generator for toast positioning
// Any app-wide element may set --app-toast-top-offset to push top-right toasts down; default 20px.
const inlineStyles = {
  root: (position: string) => ({
    position: "fixed",
    top:
      position === "top-right"
        ? "var(--app-toast-top-offset, 20px)"
        : position === "top-left"
          || position === "top-center"
          ? "20px"
          : position === "center"
            ? "50%"
            : undefined,
    right:
      position === "top-right" || position === "bottom-right"
        ? "20px"
        : undefined,
    bottom:
      position === "bottom-left"
      || position === "bottom-right"
      || position === "bottom-center"
        ? "calc(20px + env(safe-area-inset-bottom, 0px))"
        : undefined,
    left:
      position === "top-left" || position === "bottom-left"
        ? "20px"
        : position === "center"
          || position === "top-center"
          || position === "bottom-center"
          ? "50%"
          : undefined,
  }),
};

// Class generators for toast components
const classes = {
  root: (position: string) => [
    "z-[9999]",
    "p-toast",
    "p-component",
    `p-toast-${position}`,
  ],

  message: (severity?: string) => [
    "p-toast-message data-[state=open]:animate-slideIn data-[state=closed]:animate-hide data-[swipe=move]:translate-x-[var(--reka-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out] data-[swipe=end]:animate-swipeOut ",
    {
      "p-toast-message-info": severity === "info" || severity === undefined,
      "p-toast-message-warn": severity === "warn",
      "p-toast-message-error": severity === "error",
      "p-toast-message-success": severity === "success",
      "p-toast-message-secondary": severity === "secondary",
      "p-toast-message-contrast": severity === "contrast",
    },
  ],

  content: () => ["p-toast-message-content"],

  icon: (
    severity?: string,
    icons = {
      infoIcon: "pi pi-info-circle",
      warnIcon: "pi pi-exclamation-triangle",
      errorIcon: "pi pi-times-circle",
      successIcon: "pi pi-check-circle",
    },
  ) => [
    "p-toast-message-icon",
    {
      [icons.infoIcon || ""]: severity === "info",
      [icons.warnIcon || ""]: severity === "warn",
      [icons.errorIcon || ""]: severity === "error",
      [icons.successIcon || ""]: severity === "success",
    },
  ],

  text: () => ["p-toast-message-text"],
  summary: () => ["p-toast-summary"],
  detail: () => ["p-toast-detail"],
  closeButton: () => ["p-toast-close-button pi pi-times"],
  closeIcon: () => ["p-icon p-toast-close-icon"],
};

// The live audit header sits top-right on tablets: toasts go to the bottom there.
// Tenant is in the path or in the subdomain (app/router.options.ts).
const LIVE_AUDIT_PATH = /^(\/[^/]+)?\/audit-passengers\/?$/;

export function toastPositionForPath(path: string) {
  return LIVE_AUDIT_PATH.test(path) ? "bottom-right" : "top-right";
}

export const ToastStyle = {
  inlineStyles,
  classes,
};
